import { createContext, useCallback, useContext, useMemo, useState, useEffect, ReactNode } from 'react';
import { audioManager } from '../lib/audio/AudioManager';
import { getHalloweenPermanentlyDismissed, clearHalloweenSessionSuppressed } from '../lib/storage/halloween';
import { supabase } from '../lib/supabase/client';
import { getBrand } from '@/lib/branding';

// Runtime theme/sound configuration lives in POKERBLITZ's database
// (theme_configurations / theme_sound_packs, edited from its admin panel).
// Echidna has no runtime theming — SPINE is compiled CSS and its SFX are
// bundled — and its Supabase project doesn't have these tables, so firing
// the fetches there was pure 404 noise on every load (StackedStreak-x4ma).
const RUNTIME_THEME_CONFIG = (() => {
  try {
    return getBrand().key === 'poker_blitz';
  } catch {
    return false;
  }
})();

export type ThemeCategory = 'classic' | 'retro' | 'modern' | 'vintage' | 'organic' | 'neon' | 'seasonal';

export interface ThemeFonts {
  primary: string;
  secondary: string;
  display: string;
}

export interface ThemeAssets {
  cardBack?: string;
  cardSuits?: {
    hearts: string;
    diamonds: string;
    clubs: string;
    spades: string;
  };
  boardTexture?: string;
  borderPattern?: string;
  lighting?: string;
  particleEffect?: string;
}

export interface ThemeAudio {
  backgroundMusic?: string;
  soundPack: string;
}

export interface ThemeAnimations {
  cardDeal: string;
  cardFlip: string;
  handComplete: string;
  transitionSpeed: 'fast' | 'normal' | 'slow';
}

export type ThemeUnlockCondition =
  | { type: 'free' }
  | { type: 'games_played'; count: number }
  | { type: 'high_score'; score: number }
  | { type: 'achievement'; achievementId: string }
  | { type: 'premium' }
  | { type: 'seasonal'; month: number };

export interface Theme {
  id: string;
  name: string;
  category: ThemeCategory;
  unlockCondition: ThemeUnlockCondition;
  colors: {
    background: string;
    boardBackground: string;
    cardFace: string;
    cardBorder: string;
    cardSelected: string;
    primary: string;
    secondary: string;
    accent: string;
    text: {
      primary: string;
      secondary: string;
      accent: string;
    };
  };
  gradients: {
    main: string;
    board: string;
    card: string;
    button: string;
  };
  fonts: ThemeFonts;
  assets: ThemeAssets;
  audio: ThemeAudio;
  animations: ThemeAnimations;
}

export const themes: Record<string, Theme> = {
  classic: {
    id: 'classic',
    name: 'Classic Casino',
    category: 'classic',
    unlockCondition: { type: 'free' },
    colors: {
      background: 'from-emerald-900 via-emerald-800 to-emerald-900',
      boardBackground: 'from-emerald-700 to-emerald-800',
      cardFace: 'bg-white',
      cardBorder: 'border-emerald-200',
      cardSelected: 'border-amber-400 ring-amber-400',
      primary: '#059669',
      secondary: '#10b981',
      accent: '#fbbf24',
      text: {
        primary: '#ffffff',
        secondary: '#d1d5db',
        accent: '#fbbf24',
      },
    },
    gradients: {
      main: 'bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-900',
      board: 'bg-gradient-to-br from-emerald-700 to-emerald-800',
      card: 'bg-gradient-to-br from-white to-gray-50',
      button: 'bg-gradient-to-r from-emerald-600 to-emerald-700',
    },
    fonts: {
      primary: 'font-sans',
      secondary: 'font-sans',
      display: 'font-bold',
    },
    assets: {},
    audio: {
      backgroundMusic: 'classic-casino',
      soundPack: 'classic',
    },
    animations: {
      cardDeal: 'animate-slide-in',
      cardFlip: 'animate-flip',
      handComplete: 'animate-bounce',
      transitionSpeed: 'normal',
    },
  },
  vegas: {
    id: 'vegas',
    name: '🎰 Vegas Nights',
    category: 'neon',
    unlockCondition: { type: 'free' },
    colors: {
      background: 'from-purple-950 via-fuchsia-950 to-black',
      boardBackground: 'from-purple-900 to-black',
      cardFace: 'bg-gradient-to-br from-purple-900 via-fuchsia-800 to-black',
      cardBorder: 'border-fuchsia-500',
      cardSelected: 'border-pink-400 ring-pink-400',
      primary: '#c026d3',
      secondary: '#ec4899',
      accent: '#ec4899',
      text: {
        primary: '#ffffff',
        secondary: '#f9a8d4',
        accent: '#ec4899',
      },
    },
    gradients: {
      main: 'bg-gradient-to-br from-purple-950 via-fuchsia-950 to-black',
      board: 'bg-gradient-to-br from-purple-900/50 via-fuchsia-900/30 to-black/90',
      card: 'bg-gradient-to-br from-gray-900 to-black',
      button: 'bg-gradient-to-r from-pink-600 to-purple-600',
    },
    fonts: {
      primary: 'font-sans',
      secondary: 'font-mono',
      display: 'font-black tracking-wide',
    },
    assets: {
      cardBack: 'vegas-card-back',
      particleEffect: 'neon-sparkles',
    },
    audio: {
      backgroundMusic: 'vegas-nights',
      soundPack: 'vegas',
    },
    animations: {
      cardDeal: 'animate-slide-glow',
      cardFlip: 'animate-flip-glow',
      handComplete: 'animate-pulse-neon',
      transitionSpeed: 'fast',
    },
  },
  modern: {
    id: 'modern',
    name: 'Modern Dark',
    category: 'modern',
    unlockCondition: { type: 'free' },
    colors: {
      background: 'from-slate-900 via-slate-800 to-slate-900',
      boardBackground: 'from-slate-700 to-slate-800',
      cardFace: 'bg-slate-50',
      cardBorder: 'border-slate-300',
      cardSelected: 'border-blue-400 ring-blue-400',
      primary: '#334155',
      secondary: '#475569',
      accent: '#3b82f6',
      text: {
        primary: '#ffffff',
        secondary: '#cbd5e1',
        accent: '#3b82f6',
      },
    },
    gradients: {
      main: 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900',
      board: 'bg-gradient-to-br from-slate-700 to-slate-800',
      card: 'bg-gradient-to-br from-slate-50 to-slate-100',
      button: 'bg-gradient-to-r from-blue-600 to-blue-700',
    },
    fonts: {
      primary: 'font-sans',
      secondary: 'font-sans',
      display: 'font-semibold',
    },
    assets: {},
    audio: {
      backgroundMusic: 'modern-dark',
      soundPack: 'modern',
    },
    animations: {
      cardDeal: 'animate-fade-in',
      cardFlip: 'animate-flip-smooth',
      handComplete: 'animate-scale',
      transitionSpeed: 'normal',
    },
  },
  royal: {
    id: 'royal',
    name: '👑 Royal Gold',
    category: 'classic',
    unlockCondition: { type: 'free' },
    colors: {
      background: 'from-red-950 via-red-900 to-red-950',
      boardBackground: 'from-red-800 to-red-900',
      cardFace: 'bg-gradient-to-br from-yellow-50 via-amber-100 to-yellow-200',
      cardBorder: 'border-amber-200',
      cardSelected: 'border-yellow-400 ring-yellow-400',
      primary: '#991b1b',
      secondary: '#b91c1c',
      accent: '#fbbf24',
      text: {
        primary: '#ffffff',
        secondary: '#fde68a',
        accent: '#fbbf24',
      },
    },
    gradients: {
      main: 'bg-gradient-to-br from-red-950 via-red-900 to-red-950',
      board: 'bg-gradient-to-br from-red-800 to-red-900',
      card: 'bg-gradient-to-br from-amber-50 to-amber-100',
      button: 'bg-gradient-to-r from-amber-600 to-yellow-600',
    },
    fonts: {
      primary: 'font-serif',
      secondary: 'font-serif',
      display: 'font-bold',
    },
    assets: {
      cardBack: 'royal-pattern',
      boardTexture: 'damask',
      particleEffect: 'gold-dust',
      borderPattern: 'gold-filigree',
      lighting: 'chandelier',
    },
    audio: {
      backgroundMusic: 'royal-gold',
      soundPack: 'classic',
    },
    animations: {
      cardDeal: 'animate-slide-in',
      cardFlip: 'animate-flip',
      handComplete: 'animate-bounce',
      transitionSpeed: 'normal',
    },
  },
  ocean: {
    id: 'ocean',
    name: '🌊 Ocean Blue',
    category: 'organic',
    unlockCondition: { type: 'free' },
    colors: {
      background: 'from-blue-950 via-blue-900 to-cyan-950',
      boardBackground: 'from-blue-800 to-cyan-800',
      cardFace: 'bg-gradient-to-br from-cyan-100 via-blue-50 to-teal-200',
      cardBorder: 'border-cyan-200',
      cardSelected: 'border-cyan-400 ring-cyan-400',
      primary: '#0c4a6e',
      secondary: '#0e7490',
      accent: '#06b6d4',
      text: {
        primary: '#ffffff',
        secondary: '#a5f3fc',
        accent: '#06b6d4',
      },
    },
    gradients: {
      main: 'bg-gradient-to-br from-blue-950 via-blue-900 to-cyan-950',
      board: 'bg-gradient-to-br from-blue-800 to-cyan-800',
      card: 'bg-gradient-to-br from-cyan-50 to-blue-50',
      button: 'bg-gradient-to-r from-cyan-600 to-blue-600',
    },
    fonts: {
      primary: 'font-sans',
      secondary: 'font-sans',
      display: 'font-semibold',
    },
    assets: {
      cardBack: 'wave-pattern',
      boardTexture: 'water-ripples',
      particleEffect: 'bubbles',
      borderPattern: 'wave-border',
      lighting: 'underwater-rays',
    },
    audio: {
      backgroundMusic: 'ocean-blue',
      soundPack: 'modern',
    },
    animations: {
      cardDeal: 'animate-wave',
      cardFlip: 'animate-flip',
      handComplete: 'animate-ripple',
      transitionSpeed: 'slow',
    },
  },
  sunset: {
    id: 'sunset',
    name: 'Sunset Glow',
    category: 'organic',
    unlockCondition: { type: 'free' },
    colors: {
      background: 'from-orange-950 via-pink-950 to-purple-950',
      boardBackground: 'from-orange-800 to-pink-800',
      cardFace: 'bg-orange-50',
      cardBorder: 'border-orange-200',
      cardSelected: 'border-pink-400 ring-pink-400',
      primary: '#9a3412',
      secondary: '#be185d',
      accent: '#f97316',
      text: {
        primary: '#ffffff',
        secondary: '#fecaca',
        accent: '#f97316',
      },
    },
    gradients: {
      main: 'bg-gradient-to-br from-orange-950 via-pink-950 to-purple-950',
      board: 'bg-gradient-to-br from-orange-800 to-pink-800',
      card: 'bg-gradient-to-br from-orange-50 to-pink-50',
      button: 'bg-gradient-to-r from-orange-600 to-pink-600',
    },
    fonts: {
      primary: 'font-sans',
      secondary: 'font-sans',
      display: 'font-medium',
    },
    assets: {},
    audio: {
      backgroundMusic: 'sunset-glow',
      soundPack: 'modern',
    },
    animations: {
      cardDeal: 'animate-fade-in',
      cardFlip: 'animate-flip',
      handComplete: 'animate-glow',
      transitionSpeed: 'slow',
    },
  },
  midnight: {
    id: 'midnight',
    name: 'Midnight Sky',
    category: 'modern',
    unlockCondition: { type: 'free' },
    colors: {
      background: 'from-indigo-950 via-violet-950 to-purple-950',
      boardBackground: 'from-indigo-800 to-violet-800',
      cardFace: 'bg-indigo-50',
      cardBorder: 'border-indigo-200',
      cardSelected: 'border-violet-400 ring-violet-400',
      primary: '#312e81',
      secondary: '#4c1d95',
      accent: '#8b5cf6',
      text: {
        primary: '#ffffff',
        secondary: '#c4b5fd',
        accent: '#8b5cf6',
      },
    },
    gradients: {
      main: 'bg-gradient-to-br from-indigo-950 via-violet-950 to-purple-950',
      board: 'bg-gradient-to-br from-indigo-800 to-violet-800',
      card: 'bg-gradient-to-br from-indigo-50 to-violet-50',
      button: 'bg-gradient-to-r from-indigo-600 to-violet-600',
    },
    fonts: {
      primary: 'font-sans',
      secondary: 'font-sans',
      display: 'font-semibold',
    },
    assets: {},
    audio: {
      backgroundMusic: 'midnight-sky',
      soundPack: 'modern',
    },
    animations: {
      cardDeal: 'animate-fade-in',
      cardFlip: 'animate-flip',
      handComplete: 'animate-shimmer',
      transitionSpeed: 'normal',
    },
  },
  '8bit': {
    id: '8bit',
    name: '🎮 8-Bit Retro',
    category: 'retro',
    unlockCondition: { type: 'games_played', count: 50 },
    colors: {
      background: 'from-gray-900 via-slate-800 to-gray-900',
      boardBackground: 'from-blue-900 to-purple-900',
      cardFace: 'bg-yellow-50',
      cardBorder: 'border-yellow-400',
      cardSelected: 'border-red-500 ring-red-500',
      primary: '#0000FC',      // NES bright blue
      secondary: '#FCFC00',    // NES bright yellow
      accent: '#F80000',       // NES bright red
      text: {
        primary: '#FCFCFC',   // NES white
        secondary: '#FCFC00',  // NES yellow
        accent: '#F80000',     // NES red
      },
    },
    gradients: {
      main: 'bg-gray-900', // Flat dark gray - no gradients
      board: 'bg-[#0000BC]', // NES deep blue - no gradients
      card: 'bg-[#5C94FC]', // NES lighter blue - no gradients
      button: 'bg-[#0000FC]', // NES bright blue - no gradients
    },
    fonts: {
      primary: 'font-mono',
      secondary: 'font-mono',
      display: 'font-black uppercase',
    },
    assets: {
      cardBack: '8bit-pattern',
      boardTexture: 'pixel-grid',
      particleEffect: 'pixel-burst',
    },
    audio: {
      backgroundMusic: '8bit-retro',
      soundPack: '8bit',
    },
    animations: {
      cardDeal: 'animate-pixel-slide',
      cardFlip: 'animate-flip',
      handComplete: 'animate-pixel-bounce',
      transitionSpeed: 'fast',
    },
  },
  western: {
    id: 'western',
    name: '🤠 Wild West Saloon',
    category: 'vintage',
    unlockCondition: { type: 'games_played', count: 100 },
    colors: {
      background: 'from-amber-950 via-orange-950 to-stone-950',
      boardBackground: 'from-amber-900 to-orange-900',
      cardFace: 'bg-gradient-to-br from-amber-50 via-yellow-100 to-orange-200',
      cardBorder: 'border-amber-700',
      cardSelected: 'border-yellow-600 ring-yellow-600',
      primary: '#78350f',
      secondary: '#92400e',
      accent: '#d97706',
      text: {
        primary: '#fef3c7',
        secondary: '#fde68a',
        accent: '#d97706',
      },
    },
    gradients: {
      main: 'bg-gradient-to-br from-amber-950 via-orange-950 to-stone-950',
      board: 'bg-gradient-to-br from-amber-900 to-orange-900',
      card: 'bg-gradient-to-br from-amber-50 to-orange-50',
      button: 'bg-gradient-to-r from-amber-700 to-orange-700',
    },
    fonts: {
      primary: 'font-serif',
      secondary: 'font-serif',
      display: 'font-bold',
    },
    assets: {
      cardBack: 'wanted-poster',
      boardTexture: 'parchment',
      particleEffect: 'dust-cloud',
      borderPattern: 'wood-frame',
      lighting: 'candlelight',
    },
    audio: {
      backgroundMusic: 'wild-west-saloon',
      soundPack: 'western',
    },
    animations: {
      cardDeal: 'animate-western-deal',
      cardFlip: 'animate-flip',
      handComplete: 'animate-dusty-fade',
      transitionSpeed: 'slow',
    },
  },
  forest: {
    id: 'forest',
    name: '🌿 Forest Zen',
    category: 'organic',
    unlockCondition: { type: 'high_score', score: 5000 },
    colors: {
      background: 'from-emerald-950 via-green-950 to-teal-950',
      boardBackground: 'from-emerald-800 to-teal-800',
      cardFace: 'bg-gradient-to-br from-[#D4A574] via-[#B8956A] to-[#8B6F47]',
      cardBorder: 'border-emerald-300',
      cardSelected: 'border-teal-400 ring-teal-400',
      primary: '#064e3b',
      secondary: '#0f766e',
      accent: '#14b8a6',
      text: {
        primary: '#ffffff',
        secondary: '#ccfbf1',
        accent: '#14b8a6',
      },
    },
    gradients: {
      main: 'bg-gradient-to-br from-emerald-950 via-green-950 to-teal-950',
      board: 'bg-gradient-to-br from-emerald-800 to-teal-800',
      card: 'bg-gradient-to-br from-emerald-50 to-teal-50',
      button: 'bg-gradient-to-r from-emerald-600 to-teal-600',
    },
    fonts: {
      primary: 'font-sans',
      secondary: 'font-sans',
      display: 'font-medium',
    },
    assets: {
      cardBack: 'leaf-pattern',
      boardTexture: 'wood-grain',
      particleEffect: 'falling-leaves',
      borderPattern: 'leaf-border',
      lighting: 'dappled-light',
    },
    audio: {
      backgroundMusic: 'forest-zen',
      soundPack: 'natural',
    },
    animations: {
      cardDeal: 'animate-flowing-fade',
      cardFlip: 'animate-flip',
      handComplete: 'animate-leaf-flutter',
      transitionSpeed: 'slow',
    },
  },
  t800: {
    id: 't800',
    name: '🤖 Robots Are Coming',
    category: 'modern',
    unlockCondition: { type: 'high_score', score: 10000 },
    colors: {
      background: 'from-slate-950 via-gray-900 to-zinc-950',
      boardBackground: 'from-slate-800 to-zinc-800',
      cardFace: 'bg-slate-100',
      cardBorder: 'border-cyan-400',
      cardSelected: 'border-cyan-400 ring-cyan-400',
      primary: '#0f172a',
      secondary: '#475569',
      accent: '#06b6d4',
      text: {
        primary: '#f8fafc',
        secondary: '#cbd5e1',
        accent: '#06b6d4',
      },
    },
    gradients: {
      main: 'bg-gradient-to-br from-slate-950 via-gray-900 to-zinc-950',
      board: 'bg-gradient-to-br from-slate-800 via-zinc-800 to-slate-900',
      card: 'bg-gradient-to-br from-slate-100 via-gray-100 to-zinc-100',
      button: 'bg-gradient-to-r from-slate-700 via-cyan-600 to-slate-700',
    },
    fonts: {
      primary: 'font-mono',
      secondary: 'font-mono',
      display: 'font-black uppercase tracking-tight',
    },
    assets: {
      cardBack: 'chrome-circuits',
      boardTexture: 'brushed-metal',
      particleEffect: 'electric-sparks',
    },
    audio: {
      backgroundMusic: 'robots-are-coming',
      soundPack: 'modern',
    },
    animations: {
      cardDeal: 'animate-modern-fade',
      cardFlip: 'animate-flip-smooth',
      handComplete: 'animate-modern-shimmer',
      transitionSpeed: 'fast',
    },
  },
  vegas_plus: {
    id: 'vegas_plus',
    name: '💎 Vegas+ Neon Palace',
    category: 'neon',
    unlockCondition: { type: 'premium' },
    colors: {
      background: 'from-purple-950 via-fuchsia-950 to-black',
      boardBackground: 'from-purple-900 to-black',
      cardFace: 'bg-gradient-to-br from-purple-950 via-fuchsia-900 to-black',
      cardBorder: 'border-fuchsia-500',
      cardSelected: 'border-pink-400 ring-pink-400',
      primary: '#c026d3',
      secondary: '#ec4899',
      accent: '#ec4899',
      text: {
        primary: '#ffffff',
        secondary: '#f9a8d4',
        accent: '#ec4899',
      },
    },
    gradients: {
      main: 'bg-gradient-to-br from-purple-950 via-fuchsia-950 to-black',
      board: 'bg-gradient-to-br from-purple-900/60 via-fuchsia-900/40 to-black/95',
      card: 'bg-gradient-to-br from-gray-900 via-purple-950 to-black',
      button: 'bg-gradient-to-r from-pink-600 via-purple-600 to-fuchsia-600',
    },
    fonts: {
      primary: 'font-sans',
      secondary: 'font-mono',
      display: 'font-black tracking-wider uppercase',
    },
    assets: {
      cardBack: 'vegas-plus-neon',
      boardTexture: 'neon-scanlines',
      particleEffect: 'neon-trails',
    },
    audio: {
      backgroundMusic: 'vegas-plus-neon-palace',
      soundPack: 'vegas',
    },
    animations: {
      cardDeal: 'animate-slide-glow',
      cardFlip: 'animate-flip-glow',
      handComplete: 'animate-pulse-neon',
      transitionSpeed: 'fast',
    },
  },
  'spooky-halloween': {
    id: 'spooky-halloween',
    name: '🎃 Spooky Halloween',
    category: 'seasonal',
    unlockCondition: { type: 'seasonal', month: 10 },
    colors: {
      background: 'from-purple-900 via-black to-purple-900',
      boardBackground: 'from-purple-800 to-purple-900',
      cardFace: 'bg-gradient-to-br from-purple-100 via-pink-50 to-orange-100',
      cardBorder: 'border-orange-400',
      cardSelected: 'border-orange-300 ring-orange-300',
      primary: '#7c3aed',
      secondary: '#a855f7',
      accent: '#ff6b35',
      text: {
        primary: '#ffffff',
        secondary: '#e5e7eb',
        accent: '#ff6b35',
      },
    },
    gradients: {
      main: 'bg-gradient-to-br from-purple-900 via-black to-purple-900',
      board: 'bg-gradient-to-br from-purple-800 to-purple-900',
      card: 'bg-gradient-to-br from-purple-100 to-orange-50',
      button: 'bg-gradient-to-r from-orange-600 to-red-600',
    },
    fonts: {
      primary: 'font-serif',
      secondary: 'font-serif',
      display: 'font-black tracking-wider',
    },
    assets: {
      cardBack: 'spooky-halloween',
      boardTexture: 'cobweb-pattern',
      particleEffect: 'floating-ghosts',
      borderPattern: 'spooky-border',
      lighting: 'flickering-candles',
    },
    audio: {
      backgroundMusic: 'spooky-halloween',
      soundPack: 'spooky',
    },
    animations: {
      cardDeal: 'animate-ghost-float',
      cardFlip: 'animate-bat-flap',
      handComplete: 'animate-pumpkin-bounce',
      transitionSpeed: 'normal',
    },
  },
};

interface ThemeContextType {
  currentTheme: Theme;
  themeId: string;
  setTheme: (themeId: string) => void;
  resetToClassic: () => void;
  availableThemes: Theme[];
  showHalloweenModal: boolean;
  setShowHalloweenModal: (show: boolean) => void;
  showHalloweenBanner: boolean;
  setShowHalloweenBanner: (show: boolean) => void;
  previousTheme: string | null;
  refreshThemeOverrides: () => void;
}

// Deep-merge DB config row onto the hardcoded theme baseline.
// Only merges fields that are explicitly present in the override so that a
// partial DB row (e.g. only sounds were uploaded) doesn't wipe hardcoded values.
function mergeThemeWithOverride(
  base: Theme,
  override: Record<string, unknown> | undefined,
): Theme {
  if (!override) return base;
  const merged: Theme = { ...base };
  if (override.colors && typeof override.colors === 'object') {
    const oc = override.colors as Record<string, unknown>;
    merged.colors = {
      ...base.colors,
      ...oc,
      text: {
        ...base.colors.text,
        ...(oc.text && typeof oc.text === 'object'
          ? (oc.text as Record<string, string>)
          : {}),
      },
    } as Theme['colors'];
  }
  if (override.gradients && typeof override.gradients === 'object') {
    merged.gradients = { ...base.gradients, ...(override.gradients as object) } as Theme['gradients'];
  }
  if (override.fonts && typeof override.fonts === 'object') {
    merged.fonts = { ...base.fonts, ...(override.fonts as object) } as Theme['fonts'];
  }
  if (override.animations && typeof override.animations === 'object') {
    merged.animations = { ...base.animations, ...(override.animations as object) } as Theme['animations'];
  }
  if (override.unlock_condition && typeof override.unlock_condition === 'object') {
    merged.unlockCondition = override.unlock_condition as Theme['unlockCondition'];
  }
  return merged;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themeId, setThemeId] = useState<string>('classic');
  const [showHalloweenModal, setShowHalloweenModal] = useState(false);
  const [showHalloweenBanner, setShowHalloweenBanner] = useState(false);
  const [previousTheme, setPreviousTheme] = useState<string | null>(null);
  const [dbOverrides, setDbOverrides] = useState<Record<string, Record<string, unknown>>>({});

  useEffect(() => {
    try {
      const saved = localStorage.getItem('selectedTheme');
      
      // Allow themes in Capacitor - localStorage works fine in Capacitor apps
      if (saved && themes[saved]) {
        setThemeId(saved);
      } else {
        setThemeId('classic');
        try {
          localStorage.setItem('selectedTheme', 'classic');
        } catch (error) {
        }
      }
    } catch (error) {
      setThemeId('classic');
    }
  }, []);

  // Load admin-configured theme overrides from DB once on mount.
  // Failures are non-fatal — the hardcoded theme values remain active.
  // Re-runs when a 'themeConfigUpdated' event is dispatched (e.g. after admin saves).
  // useCallback so the Provider value memo and the 'themeConfigUpdated'
  // listener below get a stable identity — see perf brief P0-2 / bd m0mk.
  const loadDbOverrides = useCallback(async () => {
    if (!RUNTIME_THEME_CONFIG) return;
    try {
      const { data } = await supabase
        .from('theme_configurations')
        .select('theme_id, colors, gradients, fonts, animations, unlock_condition')
        .eq('is_active', true);

      if (data && data.length > 0) {
        const map: Record<string, Record<string, unknown>> = {};
        for (const row of data) {
          const override: Record<string, unknown> = {};
          if (row.colors)           override.colors           = row.colors;
          if (row.gradients)        override.gradients        = row.gradients;
          if (row.fonts)            override.fonts            = row.fonts;
          if (row.animations)       override.animations       = row.animations;
          if (row.unlock_condition) override.unlock_condition = row.unlock_condition;
          if (Object.keys(override).length > 0) map[row.theme_id] = override;
        }
        setDbOverrides(map);
      }
    } catch {
      // Network / RLS failure — hardcoded values remain active
    }
  }, []);

  useEffect(() => {
    loadDbOverrides();

    const handleConfigUpdate = () => loadDbOverrides();
    window.addEventListener('themeConfigUpdated', handleConfigUpdate);
    return () => window.removeEventListener('themeConfigUpdated', handleConfigUpdate);
  }, []);  

  // Initialize sound pack when theme changes, then load any admin-uploaded overrides
  useEffect(() => {
    const initializeSoundPack = async () => {
      // Run switchSoundPack and the Supabase theme_sound_packs select in
      // PARALLEL (perf brief LOAD-15 / bd xek2). They have no data
      // dependency on each other — switchSoundPack hits the bundled SFX
      // cache, the select hits the network — and pre-fix the await chain
      // was serial, adding one Supabase RTT to every theme-change.
      const switchPromise = audioManager.switchSoundPack(themeId).catch((error: unknown) => {
        console.warn('ThemeContext: Failed to initialize sound pack', error);
      });

      const overridesPromise = (async () => {
        if (!RUNTIME_THEME_CONFIG) return null;
        const { data } = await supabase
          .from('theme_sound_packs')
          .select('sound_type, file_path')
          .eq('theme_id', themeId)
          .eq('is_active', true);
        return data;
      })().catch(() => null);

      // Wait for both before continuing — switchPromise sets the active
      // sound pack identity that the override application below assumes.
      const [, data] = await Promise.all([switchPromise, overridesPromise]);

      // Load Supabase-backed sound overrides for this theme. Failures are
      // non-fatal — the game falls back to the bundled static WAV files.
      try {
        if (data && data.length > 0) {
          const paths = data.map(r => r.file_path);
          const { data: signed } = await supabase.storage
            .from('theme-sounds')
            .createSignedUrls(paths, 3600);

          const sfxOverrides = new Map<string, string>();
          const musicTrack = audioManager.getMusicTrackForTheme(themeId);

          signed?.forEach(s => {
            const row = data.find(r => r.file_path === s.path);
            if (!row || !s.signedUrl) return;
            if (row.sound_type === 'background_music') {
              audioManager.setMusicTrackOverride(musicTrack, s.signedUrl);
            } else {
              sfxOverrides.set(row.sound_type.replace(/_/g, '-'), s.signedUrl);
            }
          });
          audioManager.setSoundOverrides(sfxOverrides);

          // If no music override row exists, clear any stale override from a
          // previous theme so the static MP3 is used for this theme.
          if (!data.some(r => r.sound_type === 'background_music')) {
            audioManager.clearMusicTrackOverride(musicTrack);
          }
        } else {
          audioManager.setSoundOverrides(new Map());
          audioManager.clearMusicTrackOverride(audioManager.getMusicTrackForTheme(themeId));
        }
      } catch {
        // Network / RLS failure — static files remain active
      }
    };

    initializeSoundPack();
  }, [themeId]);

  // Halloween auto-switchover logic - runs only on mount
  useEffect(() => {
    const checkHalloweenEvent = () => {
      const now = new Date();
      const month = now.getMonth() + 1; // 1-12
      const day = now.getDate();
      const year = now.getFullYear();

      console.log('🎃 Halloween check - Current date:', { year, month, day, fullDate: now.toISOString() });

      // Halloween period: Oct 29-31
      const isHalloweenPeriod = month === 10 && day >= 29 && day <= 31;

      // Check if permanently dismissed - modal should show on every app launch unless permanently dismissed
      const permanentlyDismissed = getHalloweenPermanentlyDismissed();

      console.log('🎃 Halloween modal conditions:', { isHalloweenPeriod, permanentlyDismissed });

      const shouldShowModal = isHalloweenPeriod && !permanentlyDismissed;

      if (shouldShowModal) {
        // Save current theme before switching
        const currentThemeId = localStorage.getItem('selectedTheme') || 'classic';
        console.log('🎃 Should show modal - currentThemeId:', currentThemeId);

        // If not already on Halloween theme, switch to it and save previous theme
        if (currentThemeId !== 'spooky-halloween') {
          setPreviousTheme(currentThemeId);
          localStorage.setItem('previousThemeBeforeHalloween', currentThemeId);

          // Switch to Halloween theme
          setThemeId('spooky-halloween');
          localStorage.setItem('selectedTheme', 'spooky-halloween');

          console.log('🎃 Halloween event activated! Switched from', currentThemeId, 'to Halloween');
          console.log('🎃 Saved previous theme to localStorage:', currentThemeId);
        } else {
          // Already on Halloween theme, but ensure previous theme is saved
          const previousThemeBeforeHalloween = localStorage.getItem('previousThemeBeforeHalloween');
          if (previousThemeBeforeHalloween && previousThemeBeforeHalloween !== 'spooky-halloween') {
            setPreviousTheme(previousThemeBeforeHalloween);
            console.log('🎃 Already on Halloween theme, restored previous theme from localStorage:', previousThemeBeforeHalloween);
          } else {
            console.log('🎃 Already on Halloween theme, no previous theme found in localStorage');
          }
        }

        // Always show modal during Halloween period if not dismissed, regardless of current theme
        // This handles the case where user is already on Halloween theme from previous session
        console.log('🎃 Scheduling modal show in 1000ms');
        setTimeout(() => {
          setShowHalloweenModal(true);
          console.log('🎃 Halloween modal triggered via setTimeout - showHalloweenModal set to TRUE');
        }, 1000);
      } else {
        // Log why modal isn't showing for debugging
        if (!isHalloweenPeriod) {
          console.log('🎃 Not Halloween period (month:', month, 'day:', day, ')');
        } else if (permanentlyDismissed) {
          console.log('🎃 Halloween modal permanently dismissed');
        }
      }

      // Auto-revert after Halloween (Nov 1st or later)
      if (month === 11 && day >= 1) {
        const prevTheme = localStorage.getItem('previousThemeBeforeHalloween');
        if (prevTheme) {
          const currentTheme = localStorage.getItem('selectedTheme');
          if (currentTheme === 'spooky-halloween') {
            setThemeId(prevTheme);
            localStorage.setItem('selectedTheme', prevTheme);
            localStorage.removeItem('previousThemeBeforeHalloween');
            console.log('👻 Halloween event ended, reverting to previous theme:', prevTheme);
          }
        } else if (localStorage.getItem('selectedTheme') === 'spooky-halloween') {
          // No previous theme saved, revert to classic
          setThemeId('classic');
          localStorage.setItem('selectedTheme', 'classic');
          console.log('👻 Halloween event ended, reverting to classic theme');
        }
        // Clear Halloween session suppression and hide banner after Halloween
        clearHalloweenSessionSuppressed();
        console.log('👻 Halloween event ended, cleared session suppression');
      }
    };

    // Check on mount only
    checkHalloweenEvent();

    // Check every hour for date changes — but ONLY in the calendar window
    // that could plausibly transition Halloween state (perf brief CODE-10
    // / bd dmpv). Jan-Sep can never flip into the Oct 29-31 window or out
    // of the Nov 1+ revert window inside a single app session, so the
    // hourly tick was running 8 months a year for no reason. Pre-Oct 29
    // and post-Nov 1 we skip the interval entirely; only the Oct 29-Nov 1
    // crossover window needs the hourly check.
    const month = new Date().getMonth() + 1; // 1-12
    const needsHourlyCheck = month === 10 || month === 11;
    const interval = needsHourlyCheck
      ? setInterval(checkHalloweenEvent, 3600000)
      : null;

    return () => {
      if (interval !== null) clearInterval(interval);
    };
  }, []); // Empty deps - only run on mount

  const setTheme = useCallback(async (newThemeId: string) => {
    if (themes[newThemeId]) {
      setThemeId(newThemeId);
      try {
        localStorage.setItem('selectedTheme', newThemeId);
        window.dispatchEvent(new CustomEvent('themeChanged', { detail: newThemeId }));

        // Switch sound pack to match the new theme
        try {
          await audioManager.switchSoundPack(newThemeId);
          // Also update menu music if switching away from Halloween
          if (newThemeId !== 'spooky-halloween') {
            // Menu music will be updated automatically via getMenuTrack() when menu is active
          }
        } catch (error) {
          console.warn('ThemeContext: Failed to switch sound pack', error);
        }
      } catch (error) {
        console.warn('ThemeContext: Failed to save theme to localStorage', error);
      }
    } else {
      console.error('ThemeContext: Invalid theme ID:', newThemeId);
    }
  }, []);

  const resetToClassic = useCallback(async () => {
    setThemeId('classic');
    localStorage.setItem('selectedTheme', 'classic');
    window.dispatchEvent(new CustomEvent('themeChanged', { detail: 'classic' }));

    // Switch sound pack to classic
    try {
      await audioManager.switchSoundPack('classic');
    } catch (error) {
      console.warn('ThemeContext: Failed to switch sound pack to classic', error);
    }
  }, []);

  // Memoize the merged current theme so it only recomputes when the
  // theme id or the admin db override for that theme changes — was
  // running mergeThemeWithOverride on every render before. (P0-2 / bd m0mk.)
  const currentTheme = useMemo(
    () => mergeThemeWithOverride(themes[themeId], dbOverrides[themeId]),
    [themeId, dbOverrides],
  );

  // Memoize the 14-entry merged theme list. Pre-fix this rebuilt the
  // whole array (14 mergeThemeWithOverride calls) on every ThemeProvider
  // render — which fires on every state change anywhere in the
  // provider's children. Now only rebuilds when dbOverrides changes.
  const availableThemes = useMemo(
    () => Object.values(themes).map(t => mergeThemeWithOverride(t, dbOverrides[t.id])),
    [dbOverrides],
  );

  // Memoize the Provider value so useTheme() consumers don't re-render
  // on unrelated parent renders (per-second gameTime tick in page.tsx
  // was cascading through here too). Setters from useState are stable
  // so they don't need to be listed in deps.
  const value = useMemo<ThemeContextType>(
    () => ({
      currentTheme,
      themeId,
      setTheme,
      resetToClassic,
      availableThemes,
      showHalloweenModal,
      setShowHalloweenModal,
      showHalloweenBanner,
      setShowHalloweenBanner,
      previousTheme,
      refreshThemeOverrides: loadDbOverrides,
    }),
    [
      currentTheme,
      themeId,
      setTheme,
      resetToClassic,
      availableThemes,
      showHalloweenModal,
      showHalloweenBanner,
      previousTheme,
      loadDbOverrides,
    ],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}